import {
    computed, ref, watch, readonly, type Ref, nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDialogStore } from '@/stores/dialogStore';
import { useSnackbar } from '@/composables/useSnackbar';
import { useTranslation } from '@/composables/useTranslation';
import { SnackbarColor } from '@/types/enums/SnackbarEnums';
import type { BForm } from '@/design-system';
import type { FormDialogConfigType, FormComposableReturnType } from '@/types/composables/FormDialogComposableTypes';
import { useOnboardingStore } from '@/stores/onboardingStore';

export function useFormDialog<T extends { id: number | string }>(
    config: FormDialogConfigType<T>,
    itemId: Ref<T['id'] | null>,
): FormComposableReturnType<T> {
    const { dialogId, store, onOpen, onClose, onBeforeSubmit, afterSave, pathRouting } = config;
    const router = useRouter();
    const route = useRoute();
    const dialogStore = useDialogStore();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const formRef = ref<typeof BForm | null>(null);

    const isEditMode = computed((): boolean => {
        const editRouteName = `${pathRouting.baseRouteName}Edit`;
        const detailRouteName = `${pathRouting.baseRouteName}Detail`;
        const hasValidId = !!(
            formData.value.id !== undefined &&
            formData.value.id !== null &&
            formData.value.id !== '' &&
            formData.value.id !== 0 &&
            formData.value.id !== -1
        );

        // Detail route není edit mode
        if (route.name === detailRouteName) {
            return false;
        }

        return (route.name === editRouteName && !!route.params.id) || hasValidId;
    });

    const formData = computed((): T => store.editedItem);
    const customValidationErrors = ref<Record<string, string | boolean>>({});
    const isOpenRef = ref<boolean>(false);

    const clearCustomValidationErrors = () => {
        customValidationErrors.value = {};
    };

    const handleCancel = async () => {
        if (onClose) {
            await onClose();
        }
        clearCustomValidationErrors();

        router.push({ name: pathRouting.baseRouteName });

        dialogStore.close(dialogId);
        if (isOpenRef.value) {
            isOpenRef.value = false;
        }

        store.resetEditedItem();
    };

    const handleSubmit = async (closeAfterSave: boolean = true) => {
        const { valid: isValid, errors } = await formRef.value!.validate();
        clearCustomValidationErrors();

        if (onBeforeSubmit && isValid) {
            const customValidationResult = await onBeforeSubmit(formData.value);
            if (customValidationResult !== true) {
                customValidationErrors.value.general = customValidationResult;

                return;
            }
        }

        if (isValid) {
            const wasCreateMode = !isEditMode.value;
            let result: boolean = false;
            if (isEditMode.value) {
                result = await store.updateItem();
            } else {
                result = await store.createItem();
                if (result && !closeAfterSave) {
                    itemId.value = store.editedItem.id;
                    router.replace({
                        name: `${pathRouting.baseRouteName}Edit`,
                        params: { id: String(store.editedItem.id) },
                    });
                }
            }

            if (result && afterSave) {
                await afterSave(formData.value, isEditMode.value);
            }

            // Handle onboarding for first create
            if (result && wasCreateMode && config.onboarding) {
                const onboardingStore = useOnboardingStore();
                if (onboardingStore.shouldShow(config.onboarding.storageKey)) {
                    const createdItem = store.editedItem;

                    if (closeAfterSave) {
                        handleCancel();
                    }

                    await nextTick();
                    const { storageKey, title, successMessage, nextStepMessage, actionLabel, onAction } =
                        config.onboarding;

                    onboardingStore.show({
                        storageKey,
                        title,
                        successMessage,
                        nextStepMessage,
                        actionLabel,
                        onAction: () => onAction?.(createdItem),
                    });

                    return;
                }
            }

            if (result && closeAfterSave) {
                handleCancel();
            }
        } else {
            console.error(errors);
        }
    };

    const handleSaveOnly = async () => {
        await handleSubmit(false);
    };

    const handleSaveAndClose = async () => {
        await handleSubmit(true);
    };

    const prefillFormFromQuery = (query: Record<string, any>) => {
        if (!formRef.value) {
            return;
        }

        const formElement = formRef.value.$el as HTMLElement;
        if (!formElement) {
            return;
        }

        for (const queryKey of Object.keys(query)) {
            const queryValue = query[queryKey];

            if (queryValue === null || queryValue === undefined || queryValue === '') {
                continue;
            }

            // Primary method: Find input with matching name attribute
            const inputWithName = formElement.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
                `[name="${queryKey}"]`,
            );
            const dataType = inputWithName?.dataset?.type || 'string';

            if (inputWithName) {
                setFormDataValue(queryKey, queryValue, dataType);

                continue;
            }

            // Fallback: Check if key exists in formData
            if (Object.prototype.hasOwnProperty.call(formData.value, queryKey)) {
                setFormDataValue(queryKey, queryValue, dataType);
            }
        }
    };

    // BDialog renders its body (and therefore the BForm) lazily when it opens,
    // so formRef may still be null right after dialogStore.open(). Wait for the
    // form to actually mount before running the query prefill.
    const prefillFormWhenReady = async (query: Record<string, any>) => {
        for (let attempt = 0; attempt < 20; attempt++) {
            if (formRef.value?.$el) {
                prefillFormFromQuery(query);

                return;
            }

            await nextTick();
        }
    };

    const setFormDataValue = (key: string, value: any, dataType: string) => {
        let convertedValue: any = value;

        if (dataType === 'number') {
            const numValue = Number(value);
            if (!isNaN(numValue) && value !== '') {
                convertedValue = numValue;
            } else if (value === '' || value === null) {
                convertedValue = null;
            }
        } else if (dataType === 'boolean') {
            if (value === 'true' || value === '1' || value === 1 || value === true) {
                convertedValue = true;
            } else if (value === 'false' || value === '0' || value === 0 || value === false) {
                convertedValue = false;
            } else {
                convertedValue = Boolean(value);
            }
        } else if (dataType === 'select' || dataType === 'autocomplete') {
            if (key.endsWith('_id') || key === 'id') {
                const numValue = Number(value);
                if (!isNaN(numValue) && value !== '') {
                    convertedValue = numValue;
                } else if (value === '' || value === null) {
                    convertedValue = null;
                }
            } else {
                convertedValue = value;
            }
        } else if (Array.isArray(value)) {
            convertedValue = value;
        } else if (dataType === 'datetime' || dataType === 'date' || dataType === 'time') {
            convertedValue = value || null;
        } else if (dataType === 'file') {
            convertedValue = value;
        } else {
            convertedValue = value === null || value === undefined ? null : String(value);
        }

        store.editedItem[key as keyof T] = convertedValue;
    };

    const setupDialog = (isOpen: Ref<boolean>) => {
        isOpenRef.value = isOpen.value;

        watch(
            () => [route.name, route.params.id] as const,
            async ([routeName]) => {
                const createRouteName = `${pathRouting.baseRouteName}Create`;
                const editRouteName = `${pathRouting.baseRouteName}Edit`;
                const detailRouteName = `${pathRouting.baseRouteName}Detail`;
                const isCreateRoute = routeName === createRouteName;
                const isEditRoute = routeName === editRouteName;
                const isDetailRoute = routeName === detailRouteName;

                if (isCreateRoute || isEditRoute || isDetailRoute) {
                    if (!isOpenRef.value) {
                        isOpenRef.value = true;
                    }

                    const isDuplicated = !Object.prototype.hasOwnProperty.call(store.editedItem, 'id');

                    if (isEditRoute || isDetailRoute) {
                        const routeId = route.params.id || null;
                        const currentItemId = store.editedItem.id;

                        if (String(routeId ?? '') !== String(currentItemId ?? '')) {
                            // Nejdřív zkus najít item v items array před resetováním
                            if (routeId !== null) {
                                const existingItem = store.items.find(
                                    item => String(item.id) === String(routeId ?? ''),
                                );
                                if (existingItem) {
                                    store.editedItem = existingItem;
                                } else {
                                    store.resetEditedItem();
                                }
                            } else {
                                store.resetEditedItem();
                            }
                        }
                    } else if (isCreateRoute && !isDuplicated) {
                        store.resetEditedItem();
                    }

                    if ((isEditRoute || isDetailRoute) && route.params.id) {
                        const id = route.params.id as string;

                        // Vždy fetchujeme data - store.fetchOne() rozhodne podle hasShowEndpoint,
                        // zda provede API call nebo použije cached data
                        const success = await store.fetchOne(id);

                        if (!success) {
                            showSnackbar(t('messages.itemNotFound', { id }), SnackbarColor.Error);
                            router.push({ name: pathRouting.baseRouteName });
                            isOpenRef.value = false;

                            return;
                        }
                    }

                    if (onOpen) {
                        await onOpen();
                    }

                    await nextTick();
                    dialogStore.open(dialogId);

                    const el = document.getElementById(`${dialogId}-dialog`);
                    if (el) {
                        el.scrollTop = 0;
                    }

                    if (isCreateRoute && Object.keys(route.query).length > 0) {
                        await prefillFormWhenReady(route.query);
                    }
                } else {
                    if (dialogStore.modals[dialogId]) {
                        dialogStore.close(dialogId);
                        store.resetEditedItem();
                        isOpenRef.value = false;
                    }
                }
            },
            { immediate: true },
        );
    };

    return {
        isEditMode,
        formData,
        formRef,
        handleCancel,
        handleSubmit,
        handleSaveOnly,
        handleSaveAndClose,
        setupDialog,
        customValidationErrors: readonly(customValidationErrors),
    };
}
